import { useEffect } from 'react';
import { toast } from 'react-toastify';
import socketService from '../services/socket';
import pushNotificationService from '../services/pushNotification';
import { Deposit, Withdrawal } from '../types';

interface DepositCreatedData {
  success: boolean;
  deposit: Deposit;
  message?: string;
  timestamp?: string;
}

interface WithdrawalCreatedData {
  success: boolean;
  withdrawal: Withdrawal;
  message?: string;
  timestamp?: string;
}

export const useSocketNotifications = (isAuthenticated: boolean) => {
  useEffect(() => {
    if (!isAuthenticated) {
      socketService.disconnect();
      return;
    }

    // Connect socket
    const token = localStorage.getItem('admin_token');
    socketService.connect(token || undefined);

    // Handle deposit created
    const handleDepositCreated = async (data: DepositCreatedData) => {
      if (data.success && data.deposit) {
        const amount = new Intl.NumberFormat('en-ET', {
          style: 'currency',
          currency: 'ETB',
        }).format(data.deposit.amount);

        // Show in-app toast notification (only when tab is active/visible)
        if (!document.hidden) {
          toast.info(
            `💰 New Deposit: ${amount} from ${data.deposit.user?.username || data.deposit.phoneNumber}`,
            {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            }
          );
        }

        // Always show system tray notification if permission is granted
        if (pushNotificationService.getPermissionStatus() === 'granted') {
          try {
            const registration = await navigator.serviceWorker.ready;
            if (registration) {
              await registration.showNotification('💰 New Deposit', {
                body: `${amount} from ${data.deposit.user?.username || data.deposit.phoneNumber}`,
                icon: '/logo192.png',
                badge: '/logo192.png',
                tag: 'deposit-' + data.deposit.id,
                silent: false, // Enable sound
                requireInteraction: false,
                data: {
                  type: 'deposit',
                  id: data.deposit.id,
                  url: '/deposits',
                },
                vibrate: [200, 100, 200],
                actions: [
                  {
                    action: 'view',
                    title: 'View Deposit',
                  },
                  {
                    action: 'close',
                    title: 'Close',
                  },
                ],
              });
            }
          } catch (error) {
            console.error('Failed to show system notification:', error);
          }
        }

        // Optionally refresh deposits list
        // You can uncomment this if you want to auto-refresh the list
        // dispatch(fetchDeposits({ page: 1, limit: 10 }) as any);
      }
    };

    // Handle withdrawal created
    const handleWithdrawalCreated = async (data: WithdrawalCreatedData) => {
      if (data.success && data.withdrawal) {
        const amount = new Intl.NumberFormat('en-ET', {
          style: 'currency',
          currency: 'ETB',
        }).format(data.withdrawal.amount);

        // Show in-app toast notification (only when tab is active/visible)
        if (!document.hidden) {
          toast.warning(
            `💸 New Withdrawal Request: ${amount} from ${data.withdrawal.user?.username || data.withdrawal.phoneNumber}`,
            {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            }
          );
        }

        // Always show system tray notification if permission is granted
        if (pushNotificationService.getPermissionStatus() === 'granted') {
          try {
            const registration = await navigator.serviceWorker.ready;
            if (registration) {
              await registration.showNotification('💸 New Withdrawal Request', {
                body: `${amount} from ${data.withdrawal.user?.username || data.withdrawal.phoneNumber}`,
                icon: '/logo192.png',
                badge: '/logo192.png',
                tag: 'withdrawal-' + data.withdrawal.id,
                silent: false, // Enable sound
                requireInteraction: false,
                data: {
                  type: 'withdrawal',
                  id: data.withdrawal.id,
                  url: '/withdrawals',
                },
                vibrate: [200, 100, 200],
                actions: [
                  {
                    action: 'view',
                    title: 'View Withdrawal',
                  },
                  {
                    action: 'close',
                    title: 'Close',
                  },
                ],
              });
            }
          } catch (error) {
            console.error('Failed to show system notification:', error);
          }
        }

        // Optionally refresh withdrawals list
        // You can uncomment this if you want to auto-refresh the list
        // dispatch(fetchWithdrawals({ page: 1, limit: 10 }) as any);
      }
    };

    // Register event listeners
    socketService.onDepositCreated(handleDepositCreated);
    socketService.onWithdrawalCreated(handleWithdrawalCreated);

    // Cleanup on unmount
    return () => {
      socketService.offDepositCreated(handleDepositCreated);
      socketService.offWithdrawalCreated(handleWithdrawalCreated);
      socketService.disconnect();
    };
  }, [isAuthenticated]);
};
